import React, { useState } from 'react';
import { Card, Badge, Modal, Button } from '@/components/ui';

interface Preset {
  id: string;
  name: string;
  description: string;
  permissions: string[]; // VD: ['users.view', 'users.create']
  theme: {
    bg: string;
    border: string;
    ring: string;
    textTitle: string;
    textDesc: string;
    icon: string;
  };
}

const PRESETS: Preset[] = [
  {
    id: 'director',
    name: 'Giám đốc',
    description: 'Quyền xem tổng quan toàn bộ hệ thống, báo cáo và phê duyệt cấp cao.',
    permissions: ['dashboard.view', 'projects.view', 'estimates.view', 'reports.view', 'approvals.view', 'users.view'],
    theme: { bg: 'bg-indigo-50', border: 'border-indigo-200 hover:border-indigo-300', ring: 'ring-indigo-500/50', textTitle: 'text-indigo-700', textDesc: 'text-indigo-600/80', icon: 'text-indigo-600' }
  },
  {
    id: 'pm',
    name: 'Quản lý dự án (PM)',
    description: 'Quản lý toàn bộ vòng đời dự án, phân bổ công việc và phê duyệt.',
    permissions: ['dashboard.view', 'projects.view', 'projects.update', 'estimates.view', 'work-orders.manage', 'approvals.view'],
    theme: { bg: 'bg-blue-50', border: 'border-blue-200 hover:border-blue-300', ring: 'ring-blue-500/50', textTitle: 'text-blue-700', textDesc: 'text-blue-600/80', icon: 'text-blue-600' }
  },
  {
    id: 'qa_qc',
    name: 'Kỹ sư QA/QC',
    description: 'Kiểm soát chất lượng, theo dõi sự cố và hồ sơ chất lượng.',
    permissions: ['dashboard.view', 'projects.view', 'quality.manage', 'approvals.view'],
    theme: { bg: 'bg-emerald-50', border: 'border-emerald-200 hover:border-emerald-300', ring: 'ring-emerald-500/50', textTitle: 'text-emerald-700', textDesc: 'text-emerald-600/80', icon: 'text-emerald-600' }
  },
  {
    id: 'qs',
    name: 'Kỹ sư QS',
    description: 'Quản lý dự toán, định mức, BOQ và thanh quyết toán.',
    permissions: ['dashboard.view', 'estimates.manage', 'reports.view', 'approvals.view'],
    theme: { bg: 'bg-violet-50', border: 'border-violet-200 hover:border-violet-300', ring: 'ring-violet-500/50', textTitle: 'text-violet-700', textDesc: 'text-violet-600/80', icon: 'text-violet-600' }
  },
  {
    id: 'site_manager',
    name: 'Chỉ huy trưởng',
    description: 'Quản lý điều hành thi công trực tiếp tại công trường.',
    permissions: ['dashboard.view', 'projects.view', 'work-orders.manage', 'inventory.view', 'approvals.view', 'teams.manage'],
    theme: { bg: 'bg-orange-50', border: 'border-orange-200 hover:border-orange-300', ring: 'ring-orange-500/50', textTitle: 'text-orange-700', textDesc: 'text-orange-600/80', icon: 'text-orange-600' }
  },
  {
    id: 'warehouse',
    name: 'Thủ kho',
    description: 'Quản lý xuất nhập tồn, kho bãi và thẻ kho.',
    permissions: ['dashboard.view', 'inventory.manage', 'requests.view'],
    theme: { bg: 'bg-teal-50', border: 'border-teal-200 hover:border-teal-300', ring: 'ring-teal-500/50', textTitle: 'text-teal-700', textDesc: 'text-teal-600/80', icon: 'text-teal-600' }
  },
  {
    id: 'accountant',
    name: 'Kế toán',
    description: 'Xử lý thanh toán, dòng tiền và báo cáo tài chính.',
    permissions: ['dashboard.view', 'billing.manage', 'reports.view', 'approvals.view'],
    theme: { bg: 'bg-green-50', border: 'border-green-200 hover:border-green-300', ring: 'ring-green-500/50', textTitle: 'text-green-700', textDesc: 'text-green-600/80', icon: 'text-green-600' }
  },
  {
    id: 'secretary',
    name: 'Thư ký dự án',
    description: 'Soạn thảo, quản lý hồ sơ tài liệu và trình ký.',
    permissions: ['dashboard.view', 'documents.manage', 'requests.create', 'approvals.view'],
    theme: { bg: 'bg-pink-50', border: 'border-pink-200 hover:border-pink-300', ring: 'ring-pink-500/50', textTitle: 'text-pink-700', textDesc: 'text-pink-600/80', icon: 'text-pink-600' }
  },
  {
    id: 'supervisor',
    name: 'Giám sát',
    description: 'Theo dõi tiến độ, kiểm tra và báo cáo công việc hàng ngày.',
    permissions: ['dashboard.view', 'work-orders.manage', 'requests.create', 'quality.view'],
    theme: { bg: 'bg-lime-50', border: 'border-lime-200 hover:border-lime-300', ring: 'ring-lime-500/50', textTitle: 'text-lime-700', textDesc: 'text-lime-600/80', icon: 'text-lime-600' }
  },
  {
    id: 'site_foreman',
    name: 'Tổ trưởng',
    description: 'Quản lý công nhân và thực thi nhiệm vụ được giao.',
    permissions: ['dashboard.view', 'work-orders.view', 'work-orders.update', 'requests.create', 'teams.view'],
    theme: { bg: 'bg-rose-50', border: 'border-rose-200 hover:border-rose-300', ring: 'ring-rose-500/50', textTitle: 'text-rose-700', textDesc: 'text-rose-600/80', icon: 'text-rose-600' }
  },
  {
    id: 'equipment_manager',
    name: 'Quản lý thiết bị',
    description: 'Kiểm soát thiết bị, vật tư máy móc và lịch bảo trì.',
    permissions: ['dashboard.view', 'equipment.manage', 'maintenance.manage', 'inventory.view'],
    theme: { bg: 'bg-cyan-50', border: 'border-cyan-200 hover:border-cyan-300', ring: 'ring-cyan-500/50', textTitle: 'text-cyan-700', textDesc: 'text-cyan-600/80', icon: 'text-cyan-600' }
  },
  {
    id: 'zone_manager',
    name: 'Quản lý Khu vực',
    description: 'Phụ trách toàn bộ hoạt động theo từng khu vực (Zone).',
    permissions: ['dashboard.view', 'zones.manage', 'work-orders.view', 'teams.view'],
    theme: { bg: 'bg-fuchsia-50', border: 'border-fuchsia-200 hover:border-fuchsia-300', ring: 'ring-fuchsia-500/50', textTitle: 'text-fuchsia-700', textDesc: 'text-fuchsia-600/80', icon: 'text-fuchsia-600' }
  },
  {
    id: 'agro_manager',
    name: 'Quản lý Nông hóa phẩm',
    description: 'Kiểm soát phân bón, thuốc và kỹ thuật nông nghiệp.',
    permissions: ['dashboard.view', 'agro.manage', 'inventory.view', 'requests.create'],
    theme: { bg: 'bg-yellow-50', border: 'border-yellow-200 hover:border-yellow-300', ring: 'ring-yellow-500/50', textTitle: 'text-yellow-800', textDesc: 'text-yellow-700/80', icon: 'text-yellow-600' }
  },
  {
    id: 'staff',
    name: 'Nhân viên cơ bản',
    description: 'Chỉ xem công việc cá nhân và tạo đề xuất/nghỉ phép.',
    permissions: ['dashboard.view', 'work-orders.view', 'requests.create'],
    theme: { bg: 'bg-slate-100', border: 'border-slate-300 hover:border-slate-400', ring: 'ring-slate-500/50', textTitle: 'text-slate-700', textDesc: 'text-slate-600/80', icon: 'text-slate-600' }
  }
];

interface Props {
  selectedPermissions: string[];
  onChange: (permissions: string[]) => void;
}

export function PermissionPresetSelector({ selectedPermissions, onChange }: Props) {
  const [detailPreset, setDetailPreset] = useState<Preset | null>(null);
  
  const handleTogglePreset = (preset: Preset) => {
    // Kiểm tra xem preset này đã được chọn chưa (đã có đủ các quyền trong preset)
    const isSelected = preset.permissions.every(p => selectedPermissions.includes(p));

    if (isSelected) {
      // Gỡ bỏ preset
      onChange(selectedPermissions.filter(p => !preset.permissions.includes(p)));
    } else {
      // Áp dụng preset (merge)
      const newPerms = new Set([...selectedPermissions, ...preset.permissions]);
      onChange(Array.from(newPerms));
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-medium text-slate-800 tracking-wide">Gói quyền mẫu (Presets)</h4>
        <span className="sm:text-sm text-xs text-slate-500 italic">Chọn nhanh để tự động tick quyền</span>
      </div>
      
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {PRESETS.map(preset => {
          const isSelected = preset.permissions.every(p => selectedPermissions.includes(p));
          
          return (
            <div 
              key={preset.id}
              onClick={() => handleTogglePreset(preset)}
              className={`
                p-3 rounded-xl border cursor-pointer transition-all active:scale-95
                ${isSelected 
                  ? `${preset.theme.bg} ${preset.theme.border} ring-2 ${preset.theme.ring} shadow-sm opacity-100` 
                  : `${preset.theme.bg} border-transparent opacity-60 hover:opacity-100 hover:shadow-sm hover:border-black/5`
                }
              `}
            >
              <div className="flex items-center justify-between mb-1">
                <span className={`text-sm font-medium tracking-wide ${preset.theme.textTitle}`}>
                  {preset.name}
                </span>
                {isSelected ? (
                  <i className={`ri-checkbox-circle-fill ${preset.theme.icon} text-lg`} />
                ) : (
                  <i className={`ri-checkbox-blank-circle-line ${preset.theme.icon} text-lg opacity-50`} />
                )}
              </div>
              <p className={`text-xs ${preset.theme.textDesc} line-clamp-2`}>
                {preset.description}
              </p>
              <div className="mt-3 flex items-end justify-between">
                <div className="flex flex-wrap gap-1 max-w-[70%]">
                  {preset.permissions.slice(0, 2).map(p => (
                    <span key={p} className="text-[9px] px-1.5 py-0.5 rounded bg-black/5 text-slate-600 font-mono">
                      {p}
                    </span>
                  ))}
                  {preset.permissions.length > 2 && (
                    <span className="text-[9px] px-1.5 py-0.5 rounded bg-black/5 text-slate-600 font-mono">
                      +{preset.permissions.length - 2}
                    </span>
                  )}
                </div>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setDetailPreset(preset);
                  }}
                  className={`text-[10px] font-medium px-2 py-1 rounded transition-colors flex items-center gap-0.5 shrink-0
                    ${isSelected ? preset.theme.textTitle + ' bg-black/5 hover:bg-black/10' : 'text-slate-500 bg-slate-100 hover:bg-slate-200'}
                  `}
                >
                  Chi tiết <i className="ri-arrow-right-s-line" />
                </button>
              </div>
            </div>
          );
        })}
      </div>

      <Modal
        isOpen={!!detailPreset}
        onClose={() => setDetailPreset(null)}
        title={detailPreset ? `Chi tiết: ${detailPreset.name}` : ''}
        size="md"
        footer={
          <Button variant="ghost" onClick={() => setDetailPreset(null)}>
            Đóng
          </Button>
        }
      >
        {detailPreset && (
          <div className="space-y-4">
            <p className="sm:text-sm text-xs text-slate-600 leading-relaxed bg-slate-50 p-4 rounded-xl border border-slate-100">
              {detailPreset.description}
            </p>
            <div>
              <h4 className="text-xs font-medium text-slate-500 uppercase tracking-widest mb-3">Danh sách Đặc quyền ({detailPreset.permissions.length})</h4>
              <div className="grid grid-cols-2 gap-2">
                {detailPreset.permissions.map(p => (
                  <div key={p} className="flex items-center gap-2 p-2 rounded-lg bg-slate-50 border border-slate-100">
                    <i className="ri-shield-check-fill text-brand-500 text-sm" />
                    <span className="text-xs font-mono text-slate-700">{p}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
